import { Link } from '@tanstack/react-router'
import { ArrowUpRight, Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'

interface Props {
  invoiceId: string
  invoiceNumber: string | null
  context?: string
  canViewInvoice: boolean
  /** `inline` zet nummer en context op een regel, als tertiaire regel onder een titel. */
  variant?: 'stacked' | 'inline'
  className?: string
}

/**
 * Het factuurnummer in een lijst: klikken kopieert het nummer, het pijltje opent
 * de factuur. Kopieren is de dagelijkse handeling, dus die zit op het nummer zelf.
 */
export function InvoiceNumberCell({ invoiceId, invoiceNumber, context, canViewInvoice, variant = 'stacked', className }: Props) {
  const [copied, setCopied] = useState(false)
  const inline = variant === 'inline'

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])

  function copy() {
    if (!invoiceNumber) return
    navigator.clipboard?.writeText(invoiceNumber)
    setCopied(true)
  }

  const openLink =
    copied ? (
      <Check className="h-3.5 w-3.5 shrink-0 text-status-paid animate-fade-in" />
    ) : canViewInvoice ? (
      <Link
        to="/facturen/$id"
        params={{ id: invoiceId }}
        aria-label="Factuur openen"
        title="Factuur openen"
        className="shrink-0 rounded-sm text-ink-faint transition-opacity duration-150 hover:text-accent-700 md:opacity-0 md:focus-visible:opacity-100 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
      >
        <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    ) : null

  return (
    <span className={cn('min-w-0', className)}>
      <span className="flex items-center gap-1">
        {invoiceNumber ? (
          <Tooltip content={copied ? null : 'Klik om te kopieren'} tapToToggle={false}>
            <button
              type="button"
              onClick={copy}
              aria-label={`Factuurnummer ${invoiceNumber} kopieren`}
              className={cn(
                'num min-w-0 truncate rounded-sm font-medium transition-colors duration-150',
                inline ? 'text-xs' : 'text-sm',
                copied ? 'text-accent-700' : inline ? 'text-ink-soft hover:text-accent-700' : 'text-ink hover:text-accent-700',
              )}
            >
              {invoiceNumber}
            </button>
          </Tooltip>
        ) : (
          <span className={cn('num min-w-0 truncate font-medium text-ink-faint', inline ? 'text-xs' : 'text-sm')}>concept</span>
        )}

        {inline && context ? <span className="truncate text-xs text-ink-faint">· {context}</span> : null}
        {openLink}
      </span>
      {!inline && context ? <span className="block truncate text-xs text-ink-faint">{context}</span> : null}
    </span>
  )
}
